import {
  CommonModule,
  isPlatformBrowser,
  NgOptimizedImage,
} from '@angular/common';
import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  PLATFORM_ID,
  SecurityContext,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import {
  SearchService,
  SearchQueryResponseData,
  QueryContentType,
} from '@app/services/http/search.service';
import { AuthService } from '@services/http/auth.service';
import { HeaderLoaderComponent } from '@components/shared/loaders/header-loader/header-loader.component';
import { debounceTime, Subject, switchMap } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HostListener } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { SafePipe } from '@app/pipes/safe';
import { Router } from '@angular/router';

@Component({
  selector: 'global-search-engine',
  templateUrl: './global-search-engine.component.html',
  styleUrls: ['./global-search-engine.component.scss'],
  host: { ngSkipHydration: 'true' },
  encapsulation: ViewEncapsulation.None,
  imports: [
    TranslatePipe,
    CommonModule,
    FormsModule,
    MatIconModule,
    HeaderLoaderComponent,
    NgOptimizedImage,
    SafePipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [AuthService, SearchService],
})
export class GlobalSearchEngineComponent implements OnDestroy {
  @Input() contentType?: QueryContentType;
  @Input() placeHolderText?: string;
  @Input() autoFocus: boolean = false;

  public isLoading: boolean = false;
  public inputText: string = '';
  public results: Array<SearchQueryResponseData> = [];
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  showResults: boolean = false;
  private isBrowser: boolean = false;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  constructor(
    private searchService: SearchService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private sanitizer: DomSanitizer,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    // Only set up search subscription in browser
    afterNextRender(() => {
      if (this.autoFocus && this.searchInput) {
        this.searchInput.nativeElement.focus();
      }

      this.searchSubject
        .pipe(
          debounceTime(1000),
          switchMap((query) => {
            this.isLoading = true;
            this.cdr.markForCheck();
            return this.searchService.search({ query });
          }),
          takeUntil(this.destroy$),
        )
        .subscribe({
          next: (response: SearchQueryResponseData[]) => {
            this.results = response.map((result) => ({
              ...result,
              text: this.stripHtmlTags(result.text),
            }));
            this.results.sort((a, b) => b.score - a.score);
            const verticalIndex = this.results.findIndex(
              (item) => item.type === 'VERTICAL',
            );
            if (verticalIndex > -1) {
              const [verticalItem] = this.results.splice(verticalIndex, 1);
              this.results.unshift(verticalItem);
            }
            this.isLoading = false;
            this.showResults = this.results.length > 0;
            this.cdr.markForCheck();
          },
          error: (error) => {
            this.results = [];
            this.isLoading = false;
            this.cdr.markForCheck();
          },
        });
    });
  }

  @HostListener('document:click', ['$event']) onDocumentClick(
    event: MouseEvent,
  ): void {
    if (this.isBrowser) {
      const target = event.target as HTMLElement;

      // Exclude scroll navigation buttons and hero section interactions
      if (
        target.closest('.deep-link-btn') ||
        target.closest('.deep-link-buttons') ||
        target.closest('app-generic-button-with-arrow') ||
        target.closest('.hero-section') ||
        target.classList.contains('deep-link-btn')
      ) {
        return; // Don't interfere with scroll navigation
      }

      // Only close search results if:
      // 1. Click is outside the search component
      // 2. Click is not on a navigation link (to avoid interfering with main navigation)
      // 3. Click is not on the search input
      if (
        !target.closest('global-search-engine') &&
        target !== this.searchInput?.nativeElement &&
        !target.closest('a[href]') && // Don't interfere with navigation links
        !target.closest('button') && // Don't interfere with navigation buttons
        this.showResults
      ) {
        this.showResults = false;
      }
    }
  }

  stripHtmlTags(html: string): string {
    const sanitizedHtml =
      this.sanitizer.sanitize(SecurityContext.HTML, html) || '';

    if (this.isBrowser) {
      const div = document.createElement('div');
      div.innerHTML = sanitizedHtml;
      let text = div.textContent || div.innerText || '';
      text = text.replace(/\\n/g, ' ');
      if (!text.endsWith('...')) {
        text += '...';
      }
      return text;
    } else {
      let text = sanitizedHtml.replace(/<[^>]*>/g, '');
      text = text.replace(/\\n/g, ' ');
      if (!text.endsWith('...')) {
        text += '...';
      }
      return text;
    }
  }

  onSearch(): void {
    // Only perform search in browser environment
    if (this.isBrowser) {
      this.searchSubject.next(this.inputText);
      this.showResults = this.results.length > 0;
    }
  }

  clearResults(): void {
    this.results = [];
    this.showResults = false;
    this.inputText = '';
  }

  onImgError(event: any) {
    if (this.isBrowser && event?.target) {
      event.target.src = `/assets/svg/logo-inverted.png`;
    }
  }

  public mapContentType(type: string): string {
    switch (type) {
      case 'BLOG':
        return 'Blog';
      case 'CORE_VERTICALS':
        return 'Kategorier';
      case 'GUIDE':
        return 'Guide';
      case 'FAQ':
        return 'FAQ';
      case 'TIPS':
        return 'Tips';
      case 'PRICE':
        return 'Pris Guide';
      case 'VERTICAL':
        return 'Service';
      default:
        return type;
    }
  }

  public navigateTo(url: string): Promise<boolean> | void {
    if (!url || !this.isBrowser) {
      return;
    }

    // Try Angular router first for internal URLs
    if (url.startsWith('/')) {
      this.router.navigateByUrl(url).catch((error) => {
        // Fallback to window.location only in browser
        if (this.isBrowser && typeof window !== 'undefined') {
          window.location.href = url;
        }
      });
    } else {
      // External URLs use window.location only in browser
      if (this.isBrowser && typeof window !== 'undefined') {
        window.location.href = url;
      }
    }

    // Close search results after navigation
    this.showResults = false;
    return;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Add focus event listener to the input field
  onInputFocus(): void {
    if (this.results.length > 0) {
      this.showResults = true;
    }
  }
}
